import { useCallback, useEffect, useState } from 'react'
import {
  Shield,
  ShieldAlert,
  Clock,
  Wifi,
  Fingerprint,
  Globe,
  RefreshCw,
} from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import api from '@/services/api'
import { cn } from '@/utils/cn'

type BotStats = {
  LAYER_1_HONEYPOT: number
  LAYER_2_TIMING: number
  LAYER_3_HEADERS: number
  LAYER_4_RECAPTCHA: number
  LAYER_5_FINGERPRINT: number
  TOTAL: number
}

const EMPTY_STATS: BotStats = {
  LAYER_1_HONEYPOT: 0,
  LAYER_2_TIMING: 0,
  LAYER_3_HEADERS: 0,
  LAYER_4_RECAPTCHA: 0,
  LAYER_5_FINGERPRINT: 0,
  TOTAL: 0,
}

const LAYERS = [
  {
    id: 'LAYER_1_HONEYPOT' as const,
    icon: Globe,
    label: 'Honeypot passivo',
    desc: 'Campos ocultos no formulário',
    color: 'text-emerald-400',
  },
  {
    id: 'LAYER_2_TIMING' as const,
    icon: Clock,
    label: 'Temporal (PoW)',
    desc: 'Velocidade de envio',
    color: 'text-blue-400',
  },
  {
    id: 'LAYER_3_HEADERS' as const,
    icon: Wifi,
    label: 'Headers do navegador',
    desc: 'Detecção de spoofing',
    color: 'text-violet-400',
  },
  {
    id: 'LAYER_4_RECAPTCHA' as const,
    icon: Shield,
    label: 'reCAPTCHA v3',
    desc: 'Score de comportamento',
    color: 'text-amber-400',
  },
  {
    id: 'LAYER_5_FINGERPRINT' as const,
    icon: Fingerprint,
    label: 'Fingerprint',
    desc: 'Telemetria de hardware',
    color: 'text-red-400',
  },
]

export default function BotDashboard() {
  const [stats, setStats] = useState<BotStats>(EMPTY_STATS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await api.get('/audit/bot-stats')
      if (data.success && data.data) {
        setStats({ ...EMPTY_STATS, ...data.data })
      }
      setError(null)
    } catch {
      setError('Erro ao carregar telemetria de segurança.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchStats()
    const interval = setInterval(fetchStats, 30_000)
    return () => clearInterval(interval)
  }, [fetchStats])

  return (
    <DashboardLayout title="Painel Anti-Bot">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-8 w-8 text-amber-400" />
            <div>
              <h1 className="text-xl font-bold text-text-primary">Defesa contra automação</h1>
              <p className="text-sm text-text-muted">
                Cinco camadas de proteção no login — atualização a cada 30s.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void fetchStats()}
            className="inline-flex items-center gap-2 rounded-lg border border-glass-border px-3 py-2 text-sm text-text-muted hover:text-text-primary"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            Atualizar
          </button>
        </div>

        {error && (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </p>
        )}

        <div className="rounded-xl border border-glass-border bg-bg-card p-4">
          <p className="text-sm text-text-muted">Total de bloqueios (amostra recente)</p>
          <p className="text-3xl font-black text-text-primary">{stats.TOTAL}</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {LAYERS.map((layer) => {
            const Icon = layer.icon
            const count = stats[layer.id]
            const pct = stats.TOTAL ? Math.round((count / stats.TOTAL) * 100) : 0
            return (
              <div
                key={layer.id}
                className="rounded-xl border border-glass-border bg-bg-card p-5"
              >
                <div className="mb-3 flex items-start gap-3">
                  <div className={cn('rounded-lg bg-white/5 p-2', layer.color)}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary">{layer.label}</h3>
                    <p className="text-xs text-text-muted">{layer.desc}</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-text-primary">{count}</p>
                <p className="text-xs text-text-muted">bloqueios · {pct}%</p>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-accent transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </DashboardLayout>
  )
}
